import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@mui/material';

import TextField from '../../../../../shared/components/TextField';
import { SxPropsOverrides } from '../../../../../shared/types/markup';
import rootStore from '../../../rootStore/RootStore';
import useStripeSubscriptionPreview from '../../../subscriptionPreview/hooks/useStripeSubscriptionPreview';
import { Plan } from '../../../../../shared/helpers/constants/plans';
import { readPlanParam } from '../../../../../shared/helpers/functions/navigation';

const isPromocodeNotFoundError = (error: Error) => {
  const promocodeNotFoundRegex = /promotion code .* not found/i;

  return promocodeNotFoundRegex.test(error.message);
};

const isPromocodeGeneralError = (error: Error) => {
  const promocodeGeneralErrorRegex = /.*promotion code.*/i;

  return promocodeGeneralErrorRegex.test(error.message);
};

export default function PromoCodeInput({
  styles,
}: {
  styles?: {
    formControl?: SxPropsOverrides;
  };
}) {
  const { t } = useTranslation();
  const { checkoutStore, uiStateStore } = rootStore;
  const { generateSubscriptionPreview } = useStripeSubscriptionPreview();
  const [searchParams] = useSearchParams();
  const plan = readPlanParam(searchParams) as Plan;
  const [value, setValue] = useState('');
  const [error, setError] = useState<string>('');

  const generatePreview = async (code: string) => {
    if (!uiStateStore.annualPlanValid) {
      return;
    }

    const previewParams =
      plan === Plan.annual
        ? {
            annual: {
              area: uiStateStore.annualPlan.area,
              seats: uiStateStore.annualPlan.additionalSeats,
              planet: uiStateStore.annualPlan.planetImagery,
            },
          }
        : { monthly: true };
    const result = await generateSubscriptionPreview({
      ...previewParams,
      promocode: code,
    });

    if (result?.error) {
      if (isPromocodeNotFoundError(result.error)) {
        setError(t('checkout.promo-code.not-found', { code }));
      } else if (isPromocodeGeneralError(result.error)) {
        setError(t('checkout.promo-code.general-error', { code }));
      }
    }
  };

  const handleChange = (newValue: string) => {
    setValue(newValue);
    setError('');

    if (!newValue) {
      void generatePreview('');
    }
  };

  const handleApply = () => {
    checkoutStore.setPromocode(value);
    setError('');
    void generatePreview(value);
  };

  return (
    <TextField
      styles={{ formControl: styles?.formControl }}
      placeholder={t('checkout.promo-code.placeholder')}
      endAdornment={
        <Button
          size="small"
          sx={{ minWidth: 0, py: 0 }}
          onClick={() => handleApply()}
        >
          {t('common.apply')}
        </Button>
      }
      value={value}
      error={!!error}
      helperText={error}
      onChange={(e) => handleChange(e.target.value)}
    />
  );
}
